import { useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { useDispatch } from "react-redux";
import { setDemo } from "../redux/actions/demoActions";
import { setHeroHeight } from "../redux/actions/heightActions";
import { useWindowWidth } from "../hooks/useWindowWidth";
import slicedOrange from "../assets/sliced-orange.png";
import exitOrange from "../assets/exit-orange.svg";
import "../styles/hero.scss";

const Hero = () => {
  const [demoMessageDisplay, setDemoMessageDisplay] = useState(false);
  const [navHorizontal, setNavHorizontal] = useState(null);
  const [navAnimation, setNavAnimation] = useState(false);
  const [changeNavColor, setChangeNavColor] = useState(false);
  const [navActiveSection, setNavActiveSection] = useState("");
  const prevDemoState = JSON.parse(localStorage.getItem("demoSwitch"));
  const [demoSwitch, setDemoSwitch] = useState(prevDemoState != null ? prevDemoState : true);
  const hero_height = useSelector((state) => state.heightReducer.hero_height);
  const modifyIntake_height = useSelector((state) => state.heightReducer.modifyIntake_height);
  const intake_height = useSelector((state) => state.heightReducer.intake_height);
  const intakeHistory_height = useSelector((state) => state.heightReducer.intakeHistory_height);
  const dispatch = useDispatch();
  const heroRef = useRef(null);
  const demoSwitchRef = useRef(null);
  const navbarRef = useRef(null);
  const heroTitleDemoContainerRef = useRef(null);
  const windowWidth = useWindowWidth();

  // Sends demoSwitch value to the redux store
  useEffect(() => {
    dispatch(setDemo(demoSwitch));
  }, [demoSwitch, dispatch]);

  // Gets hero height and sends it to the redux store
  useEffect(() => {
    dispatch(setHeroHeight(heroRef.current != null && heroRef.current.getBoundingClientRect().height));
  }, [dispatch, windowWidth]);

  // Saves the demoSwitch value to the localStorage
  useEffect(() => {
    localStorage.setItem("demoSwitch", demoSwitch);
    setDemoMessageDisplay(demoSwitch);
  }, [demoSwitch]);

  // Handles listening to the nav
  useEffect(() => {
    window.addEventListener("scroll", handleNav);
    const timeoutId = windowWidth > 800 ? setTimeout(handleNav, 500) : null;
    return () => {
      window.removeEventListener("scroll", handleNav);
      clearTimeout(timeoutId);
    };
  });

  // Since hero_height is 0 at the start this helps setting the section right after the hero_height has updated to its actual height
  useEffect(() => {
    let scrolledHeight = window.scrollY + 30;
    if (scrolledHeight < hero_height) {
      setNavActiveSection("keeptrack");
    }
  }, [hero_height]);

  // Sets the switch true/false value to demoSwitch
  const handleDemoSwitch = () => {
    setDemoSwitch(demoSwitchRef.current.checked);
  };

  // Handles section selection
  const handleSections = (navMarginTop) => {
    let scrolledHeight = window.scrollY + navMarginTop;

    // Handles activating the last section when page is scrolled all the way to the bottom
    if (window.innerHeight + window.pageYOffset >= document.body.offsetHeight) {
      setNavActiveSection("weeklyNutrition");
    } else {
      if (hero_height === 0) {
        setNavActiveSection("");
      } else if (scrolledHeight > hero_height + modifyIntake_height + intake_height + intakeHistory_height) {
        setNavActiveSection("weeklyNutrition");
      } else if (scrolledHeight > hero_height + modifyIntake_height + intake_height) {
        setNavActiveSection("intakeHistory");
      } else if (scrolledHeight > hero_height + modifyIntake_height) {
        setNavActiveSection("intake");
      } else if (scrolledHeight > hero_height) {
        setNavActiveSection("modifyIntake");
      } else if (scrolledHeight < hero_height) {
        setNavActiveSection("keeptrack");
      }
    }
  };

  // Handles nav animations and margins
  const handleNav = () => {
    // Handles nav animation
    if (navAnimation) return;

    const navbarTop = navbarRef.current.getBoundingClientRect().top;
    if (windowWidth > 800) {
      if (navHorizontal && window.scrollY <= 0) {
        setNavHorizontal(false);
        setNavAnimation(true);
        setTimeout(() => setNavAnimation(false), 1000);
      } else if (!navHorizontal && navbarTop <= 0) {
        setNavHorizontal(true);
        setNavAnimation(true);
        setTimeout(() => setNavAnimation(false), 1000);
      }
    } else {
      const heroTitleBottom = heroTitleDemoContainerRef.current.getBoundingClientRect().bottom;
      if (navbarTop <= windowWidth <= 620 ? 30 : 35) {
        setNavHorizontal(true);
      }
      if (heroTitleBottom >= 0) {
        setNavHorizontal(false);
      }
    }

    // Handles calling functions that update nav colors and sections, the paramater passed is navbar margin-top that changes based on different window width
    if (windowWidth >= 1025 || (windowWidth <= 800 && windowWidth > 620)) {
      handleNavColor(35);
      handleSections(35);
    }
    if (windowWidth < 1025 && windowWidth > 800) {
      handleNavColor(25);
      handleSections(25);
    }
    if (windowWidth <= 620) {
      handleNavColor(30);
      handleSections(30);
    }
  };

  // Handles navbar active sections
  const handleNavActiveSection = (section, num) => {
    if (navActiveSection === section) {
      return `hero__navbar-item hero__navbar-item--active ${windowWidth > 800 ? `hero__navbar-item-${num}` : ""}`;
    } else {
      return `hero__navbar-item ${windowWidth > 800 ? `hero__navbar-item-${num}` : ""}`;
    }
  };

  // Handles nav color change
  const handleNavColor = (navMarginTop) => {
    if (hero_height !== 0) {
      setChangeNavColor(window.scrollY + navMarginTop >= hero_height);
    }
  };

  return windowWidth > 800 ? (
    <div className="hero" ref={heroRef}>
      <header className="hero__content">
        <h1 className="hero__title">KeepTrack</h1>
        <nav
          className={`hero__navbar ${navHorizontal != null ? `hero__navbar--${navHorizontal ? "on" : "off"}` : ""} ${
            changeNavColor ? "hero__navbar--colored" : ""
          }`}
          ref={navbarRef}
        >
          <ul className="hero__navbar-items">
            <li
              className={handleNavActiveSection("keeptrack", 1)}
              onClick={() => {
                window.scrollTo(0, 0);
              }}
            >
              <div className="hero__navbar-item-square"></div>
              <p className="hero__navbar-item-name hero__navbar-item-name-1">KeepTrack</p>
            </li>
            <li
              className={handleNavActiveSection("modifyIntake", 2)}
              onClick={() => {
                window.scrollTo(0, hero_height);
              }}
            >
              <div className="hero__navbar-item-square"></div>
              <p className="hero__navbar-item-name hero__navbar-item-name-2">Modify Intake</p>
            </li>
            <li
              className={handleNavActiveSection("intake", 3)}
              onClick={() => {
                window.scrollTo(0, hero_height + modifyIntake_height);
              }}
            >
              <div className="hero__navbar-item-square"></div>
              <p className="hero__navbar-item-name hero__navbar-item-name-3">Intake</p>
            </li>
            <li
              className={handleNavActiveSection("intakeHistory", 4)}
              onClick={() => {
                window.scrollTo(0, hero_height + modifyIntake_height + intake_height);
              }}
            >
              <div className="hero__navbar-item-square"></div>
              <p className="hero__navbar-item-name hero__navbar-item-name-4">Intake History</p>
            </li>
            <li
              className={handleNavActiveSection("weeklyNutrition", 5)}
              onClick={() => {
                window.scrollTo(0, hero_height + modifyIntake_height + intake_height + intakeHistory_height);
              }}
            >
              <div className="hero__navbar-item-square"></div>
              <p className="hero__navbar-item-name hero__navbar-item-name-5">Weekly Nutrition</p>
            </li>
          </ul>
        </nav>
        <p className="hero__description">Maintain a healthy lifestyle.</p>
        <div className="hero__buttons">
          <button
            className="hero__button hero__button-1"
            onClick={() => {
              window.scrollTo(0, hero_height);
            }}
          >
            Try it for free
          </button>
          <button
            className="hero__button hero__button-2"
            onClick={() => {
              window.scrollTo(0, hero_height + modifyIntake_height + intake_height + intakeHistory_height);
            }}
          >
            My Nutrition
          </button>
        </div>
      </header>
      <div className="hero__image-div">
        <img src={slicedOrange} className="hero__image" alt="orange" />
      </div>
      <div className="hero__demo">
        <div className={`hero__demo-message-div hero__demo-message-div--${demoMessageDisplay ? "on" : "off"}`}>
          <p className="hero__demo-message">
            You're viewing a demo version of KeepTrack, make sure to turn it off for peronal use.
          </p>
          <img
            src={exitOrange}
            alt="exit"
            className="hero__demo-message-exit"
            onClick={() => setDemoMessageDisplay(false)}
          />
        </div>
        <label className={`hero__demo-switch hero__demo-switch--${demoSwitch ? "on" : "off"}`}>
          <p className={`hero__demo-bg-letters hero__demo-bg-letters--${demoSwitch ? "on" : "off"}`}>Demo</p>
          <input
            type="checkbox"
            className="hero__demo-switch-checkbox"
            ref={demoSwitchRef}
            onChange={handleDemoSwitch}
            checked={demoSwitch}
          />
          <span className={`hero__demo-switch-slider hero__demo-switch-slider--${demoSwitch ? "on" : "off"}`}>
            {demoSwitch ? "on" : "off"}
          </span>
        </label>
      </div>
    </div>
  ) : (
    <div className="hero" ref={heroRef}>
      <header className="hero__content">
        <div className="hero__title-demo-nav-container">
          <div className="hero__title-demo-container" ref={heroTitleDemoContainerRef}>
            <h1 className="hero__title">KeepTrack</h1>
            <div className="hero__demo">
              <label className={`hero__demo-switch hero__demo-switch--${demoSwitch ? "on" : "off"}`}>
                <p className={`hero__demo-bg-letters hero__demo-bg-letters--${demoSwitch ? "on" : "off"}`}>Demo</p>
                <input
                  type="checkbox"
                  className="hero__demo-switch-checkbox"
                  ref={demoSwitchRef}
                  onChange={handleDemoSwitch}
                  checked={demoSwitch}
                />
                <span className={`hero__demo-switch-slider hero__demo-switch-slider--${demoSwitch ? "on" : "off"}`}>
                  {demoSwitch ? "on" : "off"}
                </span>
              </label>
            </div>
          </div>
          <nav
            className={`hero__navbar ${navHorizontal != null ? `hero__navbar--${navHorizontal ? "on" : "off"}` : ""} ${
              changeNavColor ? "hero__navbar--colored" : ""
            }`}
            ref={navbarRef}
          >
            <ul className="hero__navbar-items">
              <li
                className={handleNavActiveSection("keeptrack")}
                onClick={() => {
                  window.scrollTo(0, 0);
                }}
              >
                <div className="hero__navbar-item-square"></div>
                <p className="hero__navbar-item-name">
                  {windowWidth > 480 ? (
                    "KeepTrack"
                  ) : (
                    <span className="hero__navbar-item-name-vertical">K e e p T r a c k</span>
                  )}
                </p>
              </li>
              <li
                className={handleNavActiveSection("modifyIntake")}
                onClick={() => {
                  window.scrollTo(0, hero_height);
                }}
              >
                <div className="hero__navbar-item-square"></div>
                <p className="hero__navbar-item-name">
                  {windowWidth > 480 ? (
                    "Modify Intake"
                  ) : (
                    <span className="hero__navbar-item-name-vertical">M o d i f y &zwnj; &zwnj; I n t a k e</span>
                  )}
                </p>
              </li>
              <li
                className={handleNavActiveSection("intake")}
                onClick={() => {
                  window.scrollTo(0, hero_height + modifyIntake_height);
                }}
              >
                <div className="hero__navbar-item-square"></div>
                <p className="hero__navbar-item-name">
                  {windowWidth > 480 ? "Intake" : <span className="hero__navbar-item-name-vertical">I n t a k e</span>}
                </p>
              </li>
              <li
                className={handleNavActiveSection("intakeHistory")}
                onClick={() => {
                  window.scrollTo(0, hero_height + modifyIntake_height + intake_height);
                }}
              >
                <div className="hero__navbar-item-square"></div>
                <p className="hero__navbar-item-name">
                  {windowWidth > 480 ? (
                    "Intake History"
                  ) : (
                    <span className="hero__navbar-item-name-vertical">I n t a k e &zwnj; H i s t o r y</span>
                  )}
                </p>
              </li>
              <li
                className={handleNavActiveSection("weeklyNutrition")}
                onClick={() => {
                  window.scrollTo(0, hero_height + modifyIntake_height + intake_height + intakeHistory_height);
                }}
              >
                <div className="hero__navbar-item-square"></div>
                <p className="hero__navbar-item-name">
                  {windowWidth > 480 ? (
                    "Weekly Nutrition"
                  ) : (
                    <span className="hero__navbar-item-name-vertical">W e e k l y &zwnj; N u t r i t i o n</span>
                  )}
                </p>
              </li>
            </ul>
          </nav>
        </div>
        <div className="hero__image-div">
          <img src={slicedOrange} className="hero__image" alt="orange" />
        </div>
        <div className="hero__description-buttons-container">
          <p className="hero__description">Maintain a healthy lifestyle.</p>
          <div className="hero__buttons">
            <button
              className="hero__button hero__button-1"
              onClick={() => {
                window.scrollTo(0, hero_height);
              }}
            >
              Try it for free
            </button>
            <button
              className="hero__button hero__button-2"
              onClick={() => {
                window.scrollTo(0, hero_height + modifyIntake_height + intake_height + intakeHistory_height);
              }}
            >
              My Nutrition
            </button>
          </div>
        </div>
      </header>
    </div>
  );
};

export default Hero;
